import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderTemplateDto } from 'src/api/FiretrolApiClient';
import { useDocumentTemplates } from 'src/api/queries/documentTemplates/useDocumentTemplates';
import { useCreateDocumentTemplate } from 'src/api/queries/documentTemplates/useDocumentTemplateMutations';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { formatDate } from 'src/utils';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (): MRT_ColumnDef<FolderTemplateDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Template Name',
    },
    {
      id: 'office',
      accessorFn: (row) => row.office?.name ?? 'Global',
      header: 'Office',
    },
    {
      id: 'created_by',
      accessorFn: (row) => row.createdBy?.fullName ?? 'Unknown',
      header: 'Created By',
    },
    {
      id: 'updated_at',
      accessorFn: (row) => formatDate(row.updatedAt),
      header: 'Last Updated',
    },
  ];
};

export const Documents: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } = useDocumentTemplates();
  const templates = data ?? [];
  const createTemplate = useCreateDocumentTemplate();

  const handleCreate = async () => {
    const result = await createTemplate.mutateAsync();
    navigate(`/settings/documents/templates/${result.id}`);
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'settings-documents',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<FolderTemplateDto>(
    'document-templates',
    (data) => {
      return data.map((item) => ({
        ['Template Name']: item.name,
        ['Office']: item.office?.name ?? 'Global',
        ['Created By']: item.createdBy?.fullName ?? 'Unknown',
        ['Last Updated']: formatDate(item.updatedAt),
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const template = row.original;
        return (
          <Group>
            <EditActionIcon
              onClick={() =>
                navigate(`/settings/documents/templates/${template.id}`)
              }
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: templates,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={handleCreate}>Create Template</CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: templates.length,
    isLoading,
    isFetching,
    isError,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Documents' }],
        }}
        title="Documents"
        helpText="Manage document templates for organizing project files and folders."
      />

      {table}
    </>
  );
};
